/*
  Banner Scheduler — schedule guard + carousel.

  Scheduling: Shopify caches storefront HTML and Liquid's `'now'` reflects the
  time the page was last rendered, so server-side visibility can be stale. This
  re-evaluates each banner against the visitor's clock (absolute UTC epoch) every
  30s, so banners publish/expire on time even on cached pages.

  Carousel: when layout = carousel and 2+ banners are currently active, slides
  auto-rotate (configurable), with dots, prev/next arrows and native swipe.
  With 0–1 active banners there are no controls (single banner just shows).

  Each card carries: data-bsx-card, data-enabled, data-start, data-end (unix s).
  In the theme editor (Shopify.designMode) cards are never hidden and autoplay
  is off, but the carousel is still set up so the layout previews correctly.
*/
(function () {
  'use strict';

  var inEditor = window.Shopify && window.Shopify.designMode;

  function nowSeconds() {
    return Math.floor(Date.now() / 1000);
  }

  function isActive(card, now) {
    if (card.getAttribute('data-enabled') === 'false') return false;
    var s = card.getAttribute('data-start');
    var e = card.getAttribute('data-end');
    if (s && now < parseInt(s, 10)) return false;
    if (e && now > parseInt(e, 10)) return false;
    return true;
  }

  /* ---------------- Carousel controller (one per section) ---------------- */
  function getCarousel(root) {
    if (root.__bsxCar !== undefined) return root.__bsxCar;

    if (root.getAttribute('data-bsx-layout') !== 'carousel') {
      root.__bsxCar = null;
      return null;
    }

    var track = root.querySelector('[data-bsx-track]');
    if (!track) { root.__bsxCar = null; return null; }

    var dotsWrap = root.querySelector('[data-bsx-dots]');
    var prevBtn = root.querySelector('[data-bsx-prev]');
    var nextBtn = root.querySelector('[data-bsx-next]');
    var autoplay = root.getAttribute('data-bsx-autoplay') === 'true' && !inEditor;
    var speed = parseInt(root.getAttribute('data-bsx-speed'), 10) || 5000;

    var state = { count: 0, current: 0, timer: null };

    function goTo(i) {
      if (state.count === 0) return;
      state.current = (i + state.count) % state.count;
      track.scrollTo({ left: state.current * track.clientWidth, behavior: 'smooth' });
      syncDots();
    }
    function syncFromScroll() {
      if (!track.clientWidth) return;
      state.current = Math.max(0, Math.min(state.count - 1, Math.round(track.scrollLeft / track.clientWidth)));
      syncDots();
    }
    function syncDots() {
      if (!dotsWrap) return;
      var dots = dotsWrap.children;
      for (var i = 0; i < dots.length; i++) {
        dots[i].className = 'bsx__dot' + (i === state.current ? ' bsx__dot--active' : '');
      }
    }
    function start() {
      if (autoplay && state.count > 1 && !state.timer) {
        state.timer = setInterval(function () { goTo(state.current + 1); }, speed);
      }
    }
    function stop() {
      if (state.timer) { clearInterval(state.timer); state.timer = null; }
    }
    function restart() { stop(); start(); }

    /* one-time listeners */
    track.addEventListener('scroll', syncFromScroll, { passive: true });
    track.addEventListener('mouseenter', stop);
    track.addEventListener('mouseleave', start);
    track.addEventListener('touchstart', stop, { passive: true });
    track.addEventListener('touchend', start, { passive: true });
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(state.current - 1); restart(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(state.current + 1); restart(); });

    function refresh() {
      stop();
      var vis = Array.prototype.filter.call(
        track.querySelectorAll('[data-bsx-card]'),
        function (c) { return !c.hidden; }
      );
      state.count = vis.length;
      var multi = state.count > 1;

      if (dotsWrap) {
        dotsWrap.hidden = !multi;
        // Rebuild every refresh so dots (and their thumbnails) always match the
        // currently-active set of banners.
        dotsWrap.innerHTML = '';
        if (multi) {
          for (var i = 0; i < state.count; i++) {
            var d = document.createElement('button');
            d.type = 'button';
            d.className = 'bsx__dot';
            d.setAttribute('aria-label', 'Go to slide ' + (i + 1));
            (function (idx) {
              d.addEventListener('click', function () { goTo(idx); restart(); });
            })(i);
            dotsWrap.appendChild(d);
          }
        }
      }
      if (prevBtn) prevBtn.hidden = !multi;
      if (nextBtn) nextBtn.hidden = !multi;

      syncFromScroll();
      start();
    }

    root.__bsxCar = { refresh: refresh };
    return root.__bsxCar;
  }

  /* ---------------- Per-section evaluation ---------------- */
  function evaluate(root) {
    if (!inEditor) {
      var now = nowSeconds();
      var cards = root.querySelectorAll('[data-bsx-card]');
      var activeCount = 0;
      for (var i = 0; i < cards.length; i++) {
        var active = isActive(cards[i], now);
        cards[i].hidden = !active;
        if (active) activeCount++;
      }
      // Collapse the whole section when no banner is active.
      var wrapper = root.closest('.shopify-section') || root;
      wrapper.hidden = activeCount === 0;
    }

    var car = getCarousel(root);
    if (car) car.refresh();
  }

  function evaluateAll() {
    var roots = document.querySelectorAll('[data-bsx-root]');
    for (var i = 0; i < roots.length; i++) evaluate(roots[i]);
  }

  function start() {
    evaluateAll();
    if (!window.__bsxTimer) {
      window.__bsxTimer = window.setInterval(evaluateAll, 30000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) evaluateAll();
  });

  // Theme editor: re-bind on section add/change.
  document.addEventListener('shopify:section:load', start);
  document.addEventListener('shopify:section:unload', start);
})();
